import * as THREE from "three";
import { COASTER_GAUGE, coasterLines, coasterSamples, pathClosed, type PathPoint, type Scenery } from "../../prims/scenery";
import { sideOf, type Kit } from "./kit";

// A roller coaster's track (#156) along a path: two running rails on ties
// over a box spine, on white supports down to the ground, with a train
// waiting in the station at the bottom of the lift hill (#231). The path's `y` is the rail top, and each running rail
// is one long grind along the same points it's drawn through (#189, #231:
// `coasterLines` / `coasterRails` in prims/scenery.ts).

const GAUGE = COASTER_GAUGE;
const CARS = ["#ffd166", "#ef476f", "#118ab2"];

/** How many 1 m samples apart the cars stand. */
const CAR_GAP = 2;

/**
 * Where the train waits (#231): its front car at the track's lowest point,
 * the foot of the lift hill where riders would board, and the cars behind it
 * back along the track (round past the start on a closed track). Indices
 * into `coasterSamples`' samples, front car first.
 */
export function trainSpots(ys: readonly number[], closed: boolean, cars = CARS.length): number[] {
  const low = ys.reduce((k, y, i) => (y < ys[k]! ? i : k), 0);
  // A closed track's last sample is its first again, so wrap over n - 1.
  const n = closed ? ys.length - 1 : ys.length;
  return Array.from({ length: cars }, (_, k) => {
    const i = low - k * CAR_GAP;
    return closed ? ((i % n) + n) % n : Math.max(0, i);
  });
}

export const toPoints = (path: readonly PathPoint[]): THREE.Vector3[] => path.map((p) => new THREE.Vector3(p.x, p.y, p.z));

export function coaster(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const rails = s.color ?? "#e63946";
  const samples = coasterSamples(s.path!).map(({ at, dir }) => ({ at: new THREE.Vector3(at.x, at.y, at.z), dir: new THREE.Vector3(dir.x, dir.y, dir.z) }));
  const lines = coasterLines(s.path!);
  let side: THREE.Vector3 | undefined;
  const left = toPoints(lines.left);
  const right = toPoints(lines.right);
  const spine: THREE.Vector3[] = [];
  samples.forEach(({ at, dir }, i) => {
    side = sideOf(dir, side);
    const up = new THREE.Vector3().crossVectors(side, dir).normalize();
    spine.push(at.clone().addScaledVector(up, -0.35));
    // A tie across under the rails every metre.
    const tie = new THREE.Mesh(kit.geom("tie", () => new THREE.BoxGeometry(GAUGE * 2.4, 0.08, 0.16)), kit.mat("#5c677d", "metal"));
    tie.position.copy(at).addScaledVector(up, -0.1);
    tie.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(side, up, dir.clone().negate()));
    tie.castShadow = true;
    root.add(tie);
    // A support every few metres, wherever the track runs high and the right way up.
    if (i % 3 === 1 && at.y > 0.9 && up.y > 0.5) {
      const foot = new THREE.Vector3(at.x, 0, at.z);
      kit.rod(root, foot, at.clone().addScaledVector(up, -0.4), 0.16, "#f4f1ea", "paintedMetal");
      kit.cyl(root, 0.35, 0.4, 0.3, "#b9b2a6", at.x, 0, at.z, 8, "concrete");
    }
  });
  for (const [line, r, color] of [
    [left, 0.07, rails],
    [right, 0.07, rails],
    [spine, 0.16, "#f4f1ea"],
  ] as const) {
    const tube = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(line), line.length, r, 6), kit.mat(color, "paintedMetal", { roughness: 0.45 }));
    tube.castShadow = true;
    root.add(tube);
  }
  // The train, waiting in the station.
  const spots = trainSpots(
    samples.map((p) => p.at.y),
    pathClosed(s.path!),
  );
  CARS.forEach((color, k) => {
    const at = samples[spots[k]!]!;
    const carSide = sideOf(at.dir);
    const up = new THREE.Vector3().crossVectors(carSide, at.dir).normalize();
    const car = new THREE.Group();
    kit.box(car, 1.2, 0.6, 1.7, color, 0, 0.1, 0, "plastic");
    kit.box(car, 1.0, 0.5, 0.15, "#2b2d42", 0, 0.7, -0.4, "plastic");
    car.position.copy(at.at);
    car.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(carSide, up, at.dir.clone().negate()));
    root.add(car);
  });
  return root;
}
